import React, { useState, useEffect } from 'react';
import { Card } from 'react-bootstrap';
import InputField from '../../../components/InputField';
import TextAreaField from '../../../components/TextAreaField';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';
import OptionSelector from '../../../components/OptionSelector';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';
import useSubstantiveExaminationService from '../../../app/services/hooks/useSubstantiveExaminationService';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';

interface SubstantiveExaminationStepProps {
    brandRegistrationId: number;
    onUpdate: () => void;
    isEditable: boolean;
}

const SubstantiveExaminationStep: React.FC<SubstantiveExaminationStepProps> = ({
    brandRegistrationId,
    onUpdate,
    isEditable
}) => {
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const {
        getOfficialPublicationId,
        getSubstantiveExamination,
        createSubstantiveExamination,
        getInapiDataForAutofill,
        fetchingGetOfficialPublicationId,
        fetchingGetSubstantiveExamination,
        fetchingCreateSubstantiveExamination,
        fetchingInapiAutofill
    } = useSubstantiveExaminationService();

    const [officialPublicationId, setOfficialPublicationId] = useState<number | null>(null);
    const [examinationId, setExaminationId] = useState<number | null>(null);
    const [form, setForm] = useState({
        examination_result: '',
        result_detail: '',
        notification_date: ''
    });

    const [isEditMode, setIsEditMode] = useState(false);

    useEffect(() => {
        if (brandRegistrationId) {
            getOfficialPublicationId(brandRegistrationId, {
                onSuccess: (response: ServiceResponse) => {
                    if (response.data && response.data.official_publication_id) {
                        const fetchedPublicationId = response.data.official_publication_id;
                        setOfficialPublicationId(fetchedPublicationId);
                        loadExistingData(fetchedPublicationId);
                    } else {
                        // No hay publicación oficial aún
                        setIsEditMode(true);
                    }
                },
                onError: () => {
                    // Error al obtener, permitir crear
                    setIsEditMode(true);
                }
            });
        }
    }, [brandRegistrationId]);

    const loadExistingData = (pubId: number) => {
        getSubstantiveExamination(pubId, {
            onSuccess: (res) => {
                const examination = res.data.substantive_examination;
                setExaminationId(examination.id);
                setForm({
                    examination_result: examination.examination_result || '',
                    result_detail: examination.result_detail || '',
                    notification_date: examination.notification_date || ''
                });
                setIsEditMode(false);
            },
            onError: () => {
                setIsEditMode(true);
            }
        });
    };

    const handleInapiAutofill = () => {
        if (!officialPublicationId) {
            toast.error('No se encontró la publicación oficial.');
            return;
        }

        getInapiDataForAutofill(officialPublicationId, {
            onSuccess: (response: ServiceResponse) => {
                const data = response.data;
                
                if (!data.can_autofill) {
                    toast.warning('No se encontró información suficiente en INAPI para autocompletar');
                    return;
                }
                
                // Autocompletar los campos
                setForm({
                    examination_result: data.examination_result || '',
                    result_detail: data.result_detail || '',
                    notification_date: data.notification_date || ''
                });
                
                toast.success('Campos autocompletados con datos de INAPI');
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message || 'Error al obtener datos de INAPI');
            }
        });
    };

    const handleInputChange = (field: string, value: string) => {
        setForm({ ...form, [field]: value });
    };

    const handleSubmit = () => {
        if (!officialPublicationId) {
            toast.error('No se encontró la publicación oficial');
            return;
        }

        const data = {
            official_publication_id: officialPublicationId,
            ...form
        };

        createSubstantiveExamination(data, {
            onSuccess: (res) => {
                toast.success(res.message);
                setErrors({});
                setIsEditMode(false);
                onUpdate();
            },
            onError: (res) => {
                toast.error(res.message);
            },
            onFieldError: (res) => {
                setErrors(res.data);
                toast.error('Por favor corrige los errores en el formulario');
            }
        });
    };

    if (fetchingGetOfficialPublicationId || fetchingGetSubstantiveExamination) {
        return (
            <Card className="shadow-sm">
                <Card.Body>
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    const resultOptions = [
        {
            value: 'approved',
            label: 'Aprobada',
            description: 'La solicitud fue aprobada en el examen de fondo'
        },
        {
            value: 'rejected',
            label: 'Rechazada',
            description: 'La solicitud fue rechazada en el examen de fondo'
        }
    ];

    return (
        <Card className="shadow-sm">
            <Card.Body>
                {officialPublicationId && (isEditable || isEditMode) && (
                    <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <i className="fas fa-info-circle me-2"></i>
                            Puedes autocompletar estos campos con datos del historial de INAPI
                        </div>
                        <ButtonGeneral
                            type="button"
                            variant="outline"
                            size="small"
                            title={fetchingInapiAutofill ? 'Obteniendo datos...' : 'Autocompletar con INAPI'}
                            onClick={handleInapiAutofill}
                            loading={fetchingInapiAutofill}
                            disabled={fetchingInapiAutofill}
                            icon="fas fa-sync"
                            iconPosition="left"
                        />
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <h5 className="text-primary mb-3">Resultado de Solicitud</h5>
                        <OptionSelector
                            name="examination_result"
                            options={resultOptions}
                            selectedValue={form.examination_result}
                            onChange={(value) => handleInputChange('examination_result', value)}
                        />
                        {fieldErrorMessage('examination_result') && (
                            <small className="text-danger">{fieldErrorMessage('examination_result')}</small>
                        )}
                    </div>

                    <div className="mb-3">
                        <TextAreaField
                            label="Ingrese el detalle del resultado"
                            value={form.result_detail}
                            onChange={(value) => handleInputChange('result_detail', value)}
                            onFocus={() => onFocusRemove('result_detail')}
                            error={fieldErrorMessage('result_detail')}
                            rows={5}
                            placeholder="Ingrese detalles del resultado"
                            disabled={!isEditable && !isEditMode}
                        />
                    </div>

                    <div className="mb-3">
                        <InputField
                            label="Fecha de notificación de resultado"
                            type="date"
                            value={form.notification_date}
                            onChange={(value) => handleInputChange('notification_date', value)}
                            onFocus={() => onFocusRemove('notification_date')}
                            error={fieldErrorMessage('notification_date')}
                            disabled={!isEditable && !isEditMode}
                        />
                        <small className="text-muted">
                            Se debe indicar la fecha de notificación de la resolución para asegurar el cumplimiento
                            de los plazos establecidos y permitir que el proceso avance con normalidad.
                        </small>
                    </div>

                    {(isEditable || isEditMode) && (
                        <div className="d-flex justify-content-end gap-2">
                            <ButtonGeneral
                                type="button"
                                variant="primary"
                                title="Guardar"
                                onClick={handleSubmit}
                                loading={fetchingCreateSubstantiveExamination}
                            />
                        </div>
                    )}
                </form>
            </Card.Body>
        </Card>
    );
};

export default SubstantiveExaminationStep;

